import { PanelLeft, PanelLeftClose } from 'lucide-react';
import { useId } from 'react';
import { NavLink } from 'react-router-dom';

import type { CurrentUser } from '@/api/schema';
import { visibleNavigation, type NavItem } from '@/app/navigation';
import { BrandLockup } from '@/components/brand/BrandLockup';
import { cx } from '@/lib/cx';

import styles from './Sidebar.module.css';

type SidebarProps = {
  user: CurrentUser;
  mini: boolean;
  onToggleMini: () => void;
  /** Open as an overlay on small screens. */
  mobileOpen: boolean;
  onNavigate: () => void;
};

/**
 * The navigation.
 *
 * Two tiers, not three, and only one of them is clickable.
 *
 * Sections used to be buttons that expanded and collapsed. They carried an icon
 * and a label and sat at the head of their block, so they read as the primary
 * thing on offer while the actual destinations were indented beneath them
 * without icons. People clicked "Candidates" expecting candidates and got an
 * animation. Worst of all was a section holding exactly one item — clicking it
 * revealed a single link.
 *
 * So the section is now a caption: quiet, unclickable, no icon, no hit area. The
 * destinations take the icons and the brightest colour, and are the only things
 * in the panel that respond to a click. Everything clickable has an icon;
 * everything with an icon is clickable.
 */
export function Sidebar({ user, mini, onToggleMini, mobileOpen, onNavigate }: SidebarProps) {
  const groups = visibleNavigation(user);

  // Ties each list to the caption that names it, so a screen reader announces
  // "Candidates, list, 2 items" now that the caption is no longer a control.
  const navId = useId();
  const captionId = (key: string) => `${navId}-${key}`;

  // The only interactive row in the sidebar.
  const navLink = (item: NavItem, extra?: string) => {
    const Icon = item.icon;
    return (
      <NavLink
        key={item.path}
        to={item.path}
        // Exact match only: without it, /candidates stays active while on
        // /candidates/dashboard and two entries look selected at the same time.
        end
        onClick={onNavigate}
        className={({ isActive }) => cx(styles.item, extra, isActive && styles.active)}
      >
        <Icon size={17} className={styles.itemIcon} aria-hidden="true" />
        <span className={styles.itemLabel}>{item.label}</span>
      </NavLink>
    );
  };

  return (
    <aside className={cx(styles.sidebar, mini && styles.mini, mobileOpen && styles.mobileOpen)}>
      <div className={styles.top}>
        {/* Collapsed, the rail is 30px of usable width, far too narrow for a
            wordmark two and a half times as wide as it is tall, so the globe goes in
            on its own. Expanded, the full logo, and no name beside it: the artwork
            already says it. */}
        {mini ? (
          <span className={styles.glyph}>
            <BrandLockup variant="mark" height={22} />
          </span>
        ) : (
          <BrandLockup height={38} />
        )}
        <button
          type="button"
          className={styles.collapse}
          onClick={onToggleMini}
          aria-label={mini ? 'Expand navigation' : 'Collapse navigation'}
        >
          {mini ? <PanelLeft size={16} /> : <PanelLeftClose size={16} />}
        </button>
      </div>

      <nav className={styles.nav} aria-label="Main">
        {mini
          ? // Flattened to icons. Each still carries its label as an accessible name,
            // so nothing is lost to a screen reader when the labels are hidden.
            groups.flatMap((group) =>
              group.sections.flatMap((section) =>
                section.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      end
                      onClick={onNavigate}
                      title={item.label}
                      aria-label={item.label}
                      className={({ isActive }) =>
                        cx(styles.miniItem, isActive && styles.active)
                      }
                    >
                      <Icon size={17} aria-hidden="true" />
                    </NavLink>
                  );
                }),
              ),
            )
          : groups.map((group) => (
              // The group name is no longer rendered: with sections demoted into
              // the slot it used to occupy, two caption tiers over eleven links
              // is one too many. The Workspace/System boundary survives as the
              // rule drawn between groups, and the name is kept here for
              // assistive technology.
              <div
                key={group.label}
                className={styles.group}
                role="group"
                aria-label={group.label}
              >
                {group.sections.map((section) => {
                  // Every section keeps its caption, including one holding a single
                  // destination. Promoting the lone item to a bare link was tried and
                  // looked wrong: ATS became the only row in the rail with nothing
                  // above it, floating between Screenings and Meetings as though its
                  // caption had gone missing.
                  //
                  // The reason for promoting it no longer applies either. It was done
                  // while a section was a button that looked exactly like a nav row,
                  // where a caption over one child really was confusing. Captions are
                  // quiet text now, so "ATS" over "ATS candidates" reads as a label
                  // rather than as something to click, and the rail stays regular:
                  // caption, its destinations, caption, its destinations.
                  return (
                    <div key={section.key} className={styles.section}>
                      {/* Deliberately not a heading. This sits before the page's
                          only <h1>, and four <h2>s here would corrupt the
                          document outline and appear in heading-jump navigation
                          alongside real panel headings. */}
                      <p id={captionId(section.key)} className={styles.sectionLabel}>
                        {section.label}
                      </p>
                      {/* role="list" is load-bearing, not redundant: list-style:
                          none strips list semantics in Safari/VoiceOver. */}
                      <ul
                        className={styles.items}
                        role="list"
                        aria-labelledby={captionId(section.key)}
                      >
                        {section.items.map((item) => (
                          <li key={item.path}>{navLink(item)}</li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            ))}
      </nav>
    </aside>
  );
}
